import { provisionalBlockId, type AttachmentRef, type BackgroundTask, type ChatEvent, type TokenUsage, type JobStatus, type ProvisionalChatEvent } from "@rivets/core";
import { isBackgroundRequest } from "./toolPresentation";

export type ChatBlock =
  | {
      kind: "user";
      id: string;
      seq: number;
      userId: string;
      text: string;
      ts: number;
      attachments?: AttachmentRef[];
      clientRequestId?: string;
      turnId: string;
      /** False while the agent has not started this message's turn — it is
       * sitting in a queue somewhere between here and the provider. */
      delivered: boolean;
      /** True when this message was sent into a run that was already going, so
       * it queued behind the agent's current turn. Only these are editable:
       * a run's first message *is* the job, and is gone by the time it exists. */
      queuedBehindRun: boolean;
      /** Set once its author has asked for this queued message to go now: it
       * leads the queue until the agent opens its turn. */
      sendingNow?: true;
      /** Set once a message sent now has been delivered. It jumped the queue,
       * so older messages can still be waiting behind it. */
      sentNow?: true;
      /** Set once its author has deleted this queued message. Hidden from the
       * transcript and the queue unless the agent turns out to have taken it. */
      withdrawn?: true;
      /** Where a message that waited behind a run lands once the agent takes
       * it: after the event with this seq. Its own seq is when it was *sent*,
       * which is in the middle of the run it waited behind — drawn there, it
       * jumped up above everything the agent did meanwhile the moment it was
       * taken, and the work it never saw read as its answer. Read by
       * `placeDeliveredMessages`. Mirrors native `MessageDelivery.landsAfterSeq`. */
      landsAfterSeq?: number;
      /** Team messages this prompt carried to the agent, oldest first. */
      teamContext?: string[];
    }
  /** People on the task talking to each other. The agent never received it
   * unless a later prompt names it in `teamContext`. Edits and deletions fold
   * into this one block, found by `messageId`. Mirrors native `ChatBlock.team`. */
  | {
      kind: "team";
      id: string;
      seq: number;
      messageId: string;
      userId: string;
      ts: number;
      text: string;
      mentions: string[];
      edited?: true;
      /** Its author deleted it. Kept so a later edit cannot resurrect it; never drawn. */
      withdrawn?: true;
    }
  | { kind: "response-owner"; id: string; seq: number; turnId: string; userId: string; ts: number }
  | { kind: "handoff-context"; id: string; seq: number; sourceChatId: string; sourceProviderId: string; importedEventCount: number; transcriptTruncated: boolean; transferredText: string }
  | {
      kind: "assistant";
      id: string;
      seq: number;
      turnId: string;
      userId: string;
      ts: number;
      text: string;
      done: boolean;
      failed?: string;
      /** Set when a sub-agent wrote this, not the agent the user is talking to.
       * Nested under the spawning tool call rather than shown as the answer. */
      parentToolUseId?: string;
    }
  | { kind: "thinking"; id: string; seq: number; turnId: string; text: string; done: boolean }
  | {
      kind: "tool";
      id: string;
      seq: number;
      toolUseId: string;
      name: string;
      input: unknown;
      output?: string;
      isError: boolean;
      done: boolean;
      /** The sub-agent call this one was made from, when it was not the main
       * agent that made it. */
      parentToolUseId?: string;
      /** The call asked for the work to continue after it returned, so its own
       * result says nothing about whether the work finished. */
      background?: boolean;
      /** Set once the work is known to have stopped: its task reported back,
       * or the run it belongs to is over. */
      backgroundSettled?: boolean;
      /** The provider's word for how the backgrounded work ended ("completed",
       * "failed", …). Absent when only the run ending settled it. */
      backgroundStatus?: string;
    }
  | {
      kind: "job-status";
      id: string;
      seq: number;
      status: JobStatus;
      error?: string;
      ts: number;
      /** When this run first entered a non-terminal status. Held across every
       * later status update so the run's elapsed time is measured from the
       * start of the work rather than from its last lifecycle change. */
      startedAt: number;
    }
  | { kind: "usage"; id: string; seq: number; usage: TokenUsage }
  /** A task the agent left running in the background has finished. Its own row,
   * in stream order: the work it stands for may have been launched many rows up,
   * and the moment it lands is often what the agent's next words respond to. */
  | {
      kind: "background-task";
      id: string;
      seq: number;
      ts: number;
      taskId: string;
      toolUseId?: string;
      description?: string;
      status: string;
      summary?: string;
    }
  | { kind: "log"; id: string; seq: number; level: "info" | "warn" | "error"; message: string; ts: number };

export interface FoldState {
  /** Count of events already folded, from the front of the events array. */
  processedCount: number;
  /** Seq of the first event folded; a different front means a rebuild. */
  firstSeq: number;
  /** Highest seq folded so far. Since events are always processed in the
   * array's (ascending-seq) order, the folded set is exactly the events with
   * seq <= frontierSeq — this lets us detect a non-append mutation below. */
  frontierSeq: number;
  blocks: ChatBlock[];
  openToolIndexByUseId: Map<string, number>;
  responseOwnerTurnIds: Set<string>;
  /** Where each still-undelivered user message sits, so the agent event that
   * finally opens its turn settles it without re-scanning the transcript. */
  undeliveredIndexByTurnId: Map<string, number>;
  /** Whether a run is currently open, which is what makes the next message a
   * queued steer rather than the message that starts a run. */
  runOpen: boolean;
  /** Whether any backgrounded call is still unsettled. Settling has to scan the
   * transcript — a backgrounded row has no index worth keeping, since the block
   * list is spliced as runs settle — so this keeps the scan off the turn
   * boundary of every chat that never backgrounded anything. */
  hasLiveBackgroundWork: boolean;
  /** The latest list of tasks still running in the background, which is what
   * holds a run open with nothing new to show. Emptied when the run ends, since
   * no task outlives the process that was running it. */
  backgroundTasks: BackgroundTask[];
}

export function createFoldState(): FoldState {
  return {
    processedCount: 0, firstSeq: 0, frontierSeq: 0, blocks: [], openToolIndexByUseId: new Map(),
    responseOwnerTurnIds: new Set(), undeliveredIndexByTurnId: new Map(), runOpen: false,
    hasLiveBackgroundWork: false, backgroundTasks: [],
  };
}

/** Incrementally fold new ChatEvents into renderable blocks. Only the tail of
 * `blocks` (the currently-open assistant/thinking/tool block) gets a new
 * object identity per call — everything finalized keeps its old reference so
 * memoized block components don't re-render on unrelated updates.
 *
 * Assumes `events` grows by simple append in the common case. That
 * assumption breaks when a replay page lands *after* a live event with a
 * higher seq was already folded (chatStore re-sorts by seq, so the page's
 * older events get spliced in ahead of the already-processed live event).
 * We detect that by checking whether the count of events with
 * seq <= frontierSeq still matches what we've already processed; if not,
 * the prefix changed underneath us and we rebuild the fold from scratch
 * rather than risk skipping/double-processing events. */
export function foldStep(state: FoldState, events: ChatEvent[]): FoldState {
  // chatStore's events array is always sorted ascending with unique seqs, so
  // the event immediately before our old frontier (if any) uniquely
  // identifies whether that boundary is still intact: if its seq no longer
  // equals frontierSeq, something was inserted/removed ahead of it and the
  // processed prefix can no longer be trusted. The first seq catches the
  // front moving (a trim, an older page, a window restarted past a hole) —
  // including a replacement that happens to leave the length unchanged, which
  // is why this is checked before the length short-circuit.
  const boundaryEvent = state.processedCount > 0 ? events[state.processedCount - 1] : undefined;
  const needsRebuild = state.processedCount > 0
    && (boundaryEvent?.seq !== state.frontierSeq || events[0]?.seq !== state.firstSeq);
  if (!needsRebuild && events.length === state.processedCount) return state;

  const base = needsRebuild ? createFoldState() : state;
  const startIndex = needsRebuild ? 0 : state.processedCount;
  if (startIndex >= events.length) return base;

  // Clone once per batch, then mutate that private copy. The previous
  // implementation rebuilt the whole array for every pushed/replaced event,
  // making an initial replay quadratic (and workspace switches painfully
  // expensive for long-running chats). Untouched block objects still retain
  // their identity, so memoized rows keep the same rendering behaviour.
  const blocks = base.blocks.slice();
  let openToolIndexByUseId = base.openToolIndexByUseId;
  let responseOwnerTurnIds = base.responseOwnerTurnIds;
  const undeliveredIndexByTurnId = new Map(base.undeliveredIndexByTurnId);
  let runOpen = base.runOpen;
  let hasLiveBackgroundWork = base.hasLiveBackgroundWork;
  let backgroundTasks = base.backgroundTasks;

  /** The agent has opened this turn, so nothing about it is pending any more.
   * Called for every agent event except the ones a turn can carry *while*
   * queued — its author's edit, delete, or "send now". A message that queued
   * behind a run lands after `landsAfter`, not at the seq it was sent at. */
  const markDelivered = (turnId: string | undefined, landsAfter: number, opened = true) => {
    if (turnId === undefined) return;
    const index = undeliveredIndexByTurnId.get(turnId);
    undeliveredIndexByTurnId.delete(turnId);
    const block = index === undefined ? undefined : blocks[index];
    if (index === undefined || block?.kind !== "user") return;
    // A delete that lost the race with the provider: the agent has the message
    // after all, so the transcript shows it again.
    const { withdrawn, sendingNow, ...rest } = block;
    blocks[index] = {
      ...rest, delivered: true,
      ...(sendingNow && opened ? { sentNow: true as const } : {}),
      ...(withdrawn && !opened ? { withdrawn } : {}),
      ...(rest.queuedBehindRun ? { landsAfterSeq: landsAfter } : {}),
    };
  };
  /** A run cannot end with messages still waiting for it: whatever happened to
   * them, they are no longer on their way to an agent that is still working.
   * One its author deleted stays deleted — nothing opened its turn. They land
   * after the run's own last word, its terminal status. */
  const settleAllPending = (landsAfter: number) => {
    for (const turnId of [...undeliveredIndexByTurnId.keys()]) markDelivered(turnId, landsAfter, false);
  };
  /** A backgrounded call's own result arrives the moment it is launched, so its
   * task reporting back is the honest evidence that the work has stopped. Not
   * every provider reports, though, so the end of a turn settles whatever is
   * left — except a task still on the running list: a turn held open for
   * background work can still be cut short by a queued message, and the task
   * then reports into the next turn. The end of the run settles everything. */
  const settleBackgroundWork = (keepRunning: boolean) => {
    if (!hasLiveBackgroundWork) return;
    const running = keepRunning
      ? new Set(backgroundTasks.flatMap((task) => task.toolUseId === undefined ? [] : [task.toolUseId]))
      : new Set<string>();
    hasLiveBackgroundWork = false;
    for (let index = 0; index < blocks.length; index++) {
      const block = blocks[index];
      if (block?.kind !== "tool" || !block.background || block.backgroundSettled) continue;
      if (running.has(block.toolUseId)) hasLiveBackgroundWork = true;
      else blocks[index] = { ...block, backgroundSettled: true };
    }
  };
  /** The row of the call that started a task. Scanned from the tail rather than
   * indexed: the block list is spliced as runs settle, and a task can report
   * long after its call's index was last trustworthy. */
  const toolIndexFor = (toolUseId: string | undefined): number => {
    if (toolUseId === undefined) return -1;
    for (let index = blocks.length - 1; index >= 0; index--) {
      const block = blocks[index];
      if (block?.kind === "tool" && block.toolUseId === toolUseId) return index;
    }
    return -1;
  };

  /** A team message's block, scanned from the tail like `toolIndexFor`. */
  const teamIndexFor = (messageId: string): number => {
    for (let index = blocks.length - 1; index >= 0; index--) {
      const block = blocks[index];
      if (block?.kind === "team" && block.messageId === messageId) return index;
    }
    return -1;
  };

  const push = (block: ChatBlock) => {
    blocks.push(block);
  };
  const replaceAt = (index: number, block: ChatBlock) => {
    blocks[index] = block;
  };

  for (let i = startIndex; i < events.length; i++) {
    const ev = events[i];
    if (!ev) continue;
    const p = ev.payload;

    if (p.kind === "user.message") {
      push({
        kind: "user",
        id: `u-${ev.seq}`,
        seq: ev.seq,
        userId: ev.userId ?? "unknown",
        text: p.text,
        ts: ev.ts,
        turnId: ev.turnId ?? `unknown-${ev.seq}`,
        // A message with no turn is one nothing can ever be said about: no
        // agent event can settle it and no edit can name it. Treating it as
        // delivered is what keeps it out of the pinned dock forever.
        delivered: ev.turnId === undefined,
        queuedBehindRun: ev.turnId !== undefined && runOpen,
        ...(p.clientRequestId ? { clientRequestId: p.clientRequestId } : {}),
        // Omitted rather than empty when the message carried no images, so
        // block identity stays comparable with pre-attachment history.
        ...(p.attachments && p.attachments.length > 0 ? { attachments: p.attachments } : {}),
        ...(p.teamContext ? { teamContext: p.teamContext.messageIds } : {}),
      });
      if (ev.turnId) undeliveredIndexByTurnId.set(ev.turnId, blocks.length - 1);
      continue;
    }

    if (p.kind === "team.message") {
      push({
        kind: "team", id: `tm-${p.messageId}`, seq: ev.seq, messageId: p.messageId,
        userId: ev.userId ?? "unknown", ts: ev.ts, text: p.text, mentions: p.mentions,
      });
      continue;
    }
    if (p.kind === "team.message.revised" || p.kind === "team.message.withdrawn") {
      const index = teamIndexFor(p.messageId);
      const block = index >= 0 ? blocks[index] : undefined;
      if (block?.kind !== "team" || block.withdrawn) continue;
      replaceAt(index, p.kind === "team.message.withdrawn"
        ? { ...block, withdrawn: true }
        : { ...block, text: p.text, mentions: p.mentions, edited: true });
      continue;
    }

    if (p.kind === "handoff.context") {
      push({ kind: "handoff-context", id: `h-${ev.seq}`, seq: ev.seq, sourceChatId: p.sourceChatId, sourceProviderId: p.sourceProviderId, importedEventCount: p.importedEventCount, transcriptTruncated: p.transcriptTruncated, transferredText: p.transferredText });
      continue;
    }

    if (p.kind === "job.status") {
      runOpen = !isTerminalJobStatus(p.status);
      if (!runOpen) {
        settleAllPending(ev.seq);
        settleBackgroundWork(false);
        if (backgroundTasks.length > 0) backgroundTasks = [];
      }
      const activeJobIndex = findLatestOpenJob(blocks);
      if (activeJobIndex >= 0) {
        const activeJob = blocks[activeJobIndex];
        if (activeJob?.kind === "job-status") {
          const nextJob = { ...activeJob, status: p.status, error: p.error, seq: ev.seq, ts: ev.ts };
          if (isTerminalJobStatus(p.status)) {
            blocks.splice(activeJobIndex, 1);
            blocks.push(nextJob);
          } else {
            replaceAt(activeJobIndex, nextJob);
          }
        }
      } else {
        push({ kind: "job-status", id: `j-${ev.seq}`, seq: ev.seq, status: p.status, error: p.error, ts: ev.ts, startedAt: ev.ts });
      }
      continue;
    }

    // p.kind === "agent"
    const ae = p.event;
    // An author's edit, delete, or "send now" are the agent events that reach
    // a turn *before* the provider does, so none of them introduces a responder
    // or counts as delivery. Everything else means the agent has the message.
    if (ae.type === "turn.message.revised" || ae.type === "turn.message.withdrawn" || ae.type === "turn.message.prioritized") {
      const index = ev.turnId === undefined ? undefined : undeliveredIndexByTurnId.get(ev.turnId);
      const queued = index === undefined ? undefined : blocks[index];
      if (index === undefined || queued?.kind !== "user") continue;
      if (ae.type === "turn.message.revised") replaceAt(index, { ...queued, text: ae.text });
      else if (ae.type === "turn.message.prioritized") replaceAt(index, { ...queued, sendingNow: true });
      // Kept rather than spliced out: the provider may already have taken the
      // turn, and its first agent event then brings the message back.
      else replaceAt(index, { ...queued, withdrawn: true });
      continue;
    }
    // Delivered by the agent's first event for the turn: the message lands
    // just before it.
    markDelivered(ev.turnId, ev.seq - 1);
    if (ev.turnId && ev.userId && !responseOwnerTurnIds.has(ev.turnId)) {
      responseOwnerTurnIds = new Set(responseOwnerTurnIds);
      responseOwnerTurnIds.add(ev.turnId);
      push({ kind: "response-owner", id: `owner-${ev.turnId}`, seq: ev.seq, turnId: ev.turnId, userId: ev.userId, ts: ev.ts });
    }
    // The agent's newest block, looking past people talking meanwhile: a team
    // message that lands mid-paragraph must not cut the agent's text in two.
    // The paragraph keeps growing above it, so the message reads as said at
    // the end of what the agent was writing.
    let lastIndex = blocks.length - 1;
    while (lastIndex >= 0 && blocks[lastIndex]?.kind === "team") lastIndex--;
    const last = lastIndex >= 0 ? blocks[lastIndex] : undefined;

    switch (ae.type) {
      case "session.created":
      case "turn.started":
        // Session creation and turn boundaries are transparent lifecycle
        // metadata: the agent's provider and session id are plumbing, not
        // something the reader needs on the status row.
        break;

      case "text.delta":
        if (last && last.kind === "assistant" && !last.done && last.turnId === ev.turnId
          && last.parentToolUseId === undefined) {
          replaceAt(lastIndex, { ...last, text: last.text + ae.text });
        } else {
          push({ kind: "assistant", id: `a-${ev.seq}`, seq: ev.seq, turnId: ev.turnId ?? `unknown-${ev.seq}`, userId: ev.userId ?? "unknown", ts: ev.ts, text: ae.text, done: false });
        }
        break;

      case "text.done":
        // Claude follows text deltas with an authoritative full-text snapshot,
        // while Codex can emit several complete commentary/final messages in
        // one turn. Coalesce only when this is actually the same text block;
        // otherwise preserve the new message (notably Codex's final answer).
        //
        // Either way the block is *finished*: `text.done` is the provider
        // saying so. Waiting for `turn.completed` to mark it left every
        // paragraph of a running turn flagged as still streaming, and the
        // transcript drew a blinking caret on each one.
        {
          // A sub-agent's prose never merges with the main agent's: the two are
          // different speakers, and only one of them is answering the user.
          const parent = ae.parentToolUseId;
          const mergeable = last?.kind === "assistant" && !last.done && last.turnId === ev.turnId
            && last.parentToolUseId === parent;
          if (mergeable && last.kind === "assistant"
            && (ae.text.startsWith(last.text) || last.text.startsWith(ae.text))) {
            replaceAt(lastIndex, { ...last, text: ae.text, done: true });
          } else {
            if (mergeable && last.kind === "assistant") replaceAt(lastIndex, { ...last, done: true });
            push({
              kind: "assistant", id: `a-${ev.seq}`, seq: ev.seq, turnId: ev.turnId ?? `unknown-${ev.seq}`,
              userId: ev.userId ?? "unknown", ts: ev.ts, text: ae.text, done: true,
              ...(parent === undefined ? {} : { parentToolUseId: parent }),
            });
          }
        }
        break;

      case "thinking.delta":
        if (last && last.kind === "thinking" && !last.done && last.turnId === ev.turnId) {
          replaceAt(lastIndex, { ...last, text: last.text + ae.text });
        } else {
          push({ kind: "thinking", id: `t-${ev.seq}`, seq: ev.seq, turnId: ev.turnId ?? `unknown-${ev.seq}`, text: ae.text, done: false });
        }
        break;

      case "tool.started": {
        push({
          kind: "tool",
          id: `tool-${ae.toolUseId}`,
          seq: ev.seq,
          toolUseId: ae.toolUseId,
          name: ae.name,
          input: ae.input,
          done: false,
          isError: false,
          ...(ae.parentToolUseId === undefined ? {} : { parentToolUseId: ae.parentToolUseId }),
          ...(isBackgroundRequest(ae.input) ? { background: true } : {}),
        });
        if (isBackgroundRequest(ae.input)) hasLiveBackgroundWork = true;
        openToolIndexByUseId = new Map(openToolIndexByUseId);
        openToolIndexByUseId.set(ae.toolUseId, blocks.length - 1);
        break;
      }

      case "tool.completed": {
        const idx = openToolIndexByUseId.get(ae.toolUseId);
        const toolBlock = idx !== undefined ? blocks[idx] : undefined;
        if (idx !== undefined && toolBlock?.kind === "tool") {
          replaceAt(idx, {
            ...toolBlock, output: ae.output, isError: ae.isError, done: true,
            ...(toolBlock.parentToolUseId === undefined && ae.parentToolUseId !== undefined
              ? { parentToolUseId: ae.parentToolUseId } : {}),
          });
        }
        break;
      }

      case "turn.completed":
        for (let index = 0; index < blocks.length; index++) {
          const block = blocks[index];
          if (block?.kind === "assistant" && !block.done && block.turnId === ev.turnId) blocks[index] = { ...block, done: true };
          if (block?.kind === "thinking" && !block.done && block.turnId === ev.turnId) blocks[index] = { ...block, done: true };
        }
        settleBackgroundWork(true);
        if (ae.usage) push({ kind: "usage", id: `usage-${ev.seq}`, seq: ev.seq, usage: ae.usage });
        break;

      case "turn.failed": {
        let reported = false;
        for (let index = 0; index < blocks.length; index++) {
          const block = blocks[index];
          if (block?.kind === "assistant" && !block.done && block.turnId === ev.turnId) {
            blocks[index] = { ...block, done: true, failed: ae.error };
            reported = true;
          }
          if (block?.kind === "thinking" && !block.done && block.turnId === ev.turnId) blocks[index] = { ...block, done: true };
        }
        // A turn that emitted its final message and *then* failed has no
        // unfinished block left to carry the error — `text.done` closes a block
        // the moment the provider says it is complete. The error still has to
        // land somewhere the reader is looking, so it goes on the last thing
        // that turn said.
        settleBackgroundWork(true);
        if (!reported) {
          for (let index = blocks.length - 1; index >= 0; index--) {
            const block = blocks[index];
            if (block?.kind === "assistant" && block.turnId === ev.turnId) {
              blocks[index] = { ...block, failed: ae.error };
              break;
            }
          }
        }
        break;
      }

      case "background.tasks":
        backgroundTasks = ae.tasks;
        // The provider names the call behind each task, which is firmer than
        // guessing from its input: a row it names is backgrounded, whatever the
        // call asked for.
        for (const task of ae.tasks) {
          const index = toolIndexFor(task.toolUseId);
          const block = index >= 0 ? blocks[index] : undefined;
          if (block?.kind === "tool" && !block.background) {
            replaceAt(index, { ...block, background: true });
            hasLiveBackgroundWork = true;
          }
        }
        break;

      case "background.task.completed": {
        push({
          kind: "background-task", id: `bg-${ev.seq}`, seq: ev.seq, ts: ev.ts, taskId: ae.taskId, status: ae.status,
          ...(ae.toolUseId === undefined ? {} : { toolUseId: ae.toolUseId }),
          ...(ae.description === undefined ? {} : { description: ae.description }),
          ...(ae.summary === undefined ? {} : { summary: ae.summary }),
        });
        // The call's own row stops claiming the work is going. It keeps the
        // outcome even when the turn's end already settled it.
        const index = toolIndexFor(ae.toolUseId);
        const block = index >= 0 ? blocks[index] : undefined;
        if (block?.kind === "tool") {
          replaceAt(index, { ...block, background: true, backgroundSettled: true, backgroundStatus: ae.status });
        }
        break;
      }

      case "log":
        push({ kind: "log", id: `log-${ev.seq}`, seq: ev.seq, level: ae.level, message: ae.message, ts: ev.ts });
        break;
    }
  }

  const lastEvent = events[events.length - 1];
  return {
    processedCount: events.length,
    firstSeq: events[0]?.seq ?? 0,
    frontierSeq: lastEvent ? lastEvent.seq : base.frontierSeq,
    blocks,
    openToolIndexByUseId,
    responseOwnerTurnIds,
    undeliveredIndexByTurnId,
    runOpen,
    hasLiveBackgroundWork,
    backgroundTasks,
  };
}

/**
 * The confirmed fold with unconfirmed local events folded on after it.
 *
 * Provisionals are a suffix over a *copy* of the confirmed state: `foldStep`
 * never mutates the state it is given, so the incremental confirmed fold keeps
 * its identities and its frontier, and promotion (which only ever removes
 * provisionals and appends confirmed events) never forces it to rebuild. The
 * result is thrown away on the next change.
 *
 * Each provisional stands in at a seq just past the confirmed frontier. A
 * block one of them *creates* is renamed `p-<eventId>` so it can never collide
 * with a confirmed block's id; a confirmed block one of them only *extends*
 * (the streaming assistant text the relay has partly confirmed) keeps its own.
 * `state` must be the fold of `confirmed`.
 */
export function foldProvisionalSuffix(
  state: FoldState,
  confirmed: ChatEvent[],
  provisional: readonly ProvisionalChatEvent[],
): FoldState {
  if (provisional.length === 0) return state;
  const base = state.frontierSeq;
  const eventIdBySeq = new Map<number, string>();
  const suffix: ChatEvent[] = provisional.map((event, index) => {
    const seq = base + index + 1;
    eventIdBySeq.set(seq, event.eventId);
    const { provisional: _provisional, ...rest } = event;
    return { ...rest, seq };
  });
  const folded = foldStep(state, confirmed.concat(suffix));
  let blocks: ChatBlock[] | null = null;
  for (let index = 0; index < folded.blocks.length; index++) {
    const block = folded.blocks[index]!;
    const eventId = eventIdBySeq.get(block.seq);
    if (eventId === undefined) continue;
    blocks ??= folded.blocks.slice();
    blocks[index] = { ...block, id: provisionalBlockId(eventId) };
  }
  return blocks ? { ...folded, blocks } : folded;
}

function isTerminalJobStatus(status: JobStatus): boolean {
  return status === "completed" || status === "failed" || status === "canceled";
}

export interface RunTiming {
  startedAt: number;
  /** Absent while the run is still going. */
  endedAt?: number;
}

/** Timing of the transcript's most recent run. A run's status block is folded
 * in place while it is active and moved to the tail once it settles, so the
 * last one in the list is always the newest run. */
export function latestRunTiming(blocks: ChatBlock[]): RunTiming | undefined {
  for (let index = blocks.length - 1; index >= 0; index--) {
    const block = blocks[index];
    if (block?.kind !== "job-status") continue;
    return isTerminalJobStatus(block.status)
      ? { startedAt: block.startedAt, endedAt: block.ts }
      : { startedAt: block.startedAt };
  }
  return undefined;
}

/**
 * Messages waiting behind the agent's current turn, in the order they will
 * reach it: any its author asked to send now lead, then the rest oldest first.
 *
 * They are pinned below the transcript rather than left where they landed, so
 * an agent that keeps working does not bury the thing it has not read yet.
 * A run's opening message is not one of them: nothing is ahead of it, so it
 * stays in the transcript from Send to reply. Lifting it out while the worker
 * picked the job up made it jump from the transcript to the queue and back.
 *
 * The scan walks back from the tail and stops at the first settled message or
 * run opener: everything queued is, by construction, newer than both.
 */
export function undeliveredUserBlocks(blocks: ChatBlock[]): Array<Extract<ChatBlock, { kind: "user" }>> {
  const pending: Array<Extract<ChatBlock, { kind: "user" }>> = [];
  for (let index = blocks.length - 1; index >= 0; index--) {
    const block = blocks[index];
    if (block?.kind !== "user") continue;
    // A message sent now is delivered ahead of older ones still waiting, so it
    // does not mark where the queue ends.
    if (block.delivered) {
      if (block.sentNow) continue;
      break;
    }
    if (!block.queuedBehindRun) break;
    if (!block.withdrawn) pending.push(block);
  }
  pending.reverse();
  return pending.some((block) => block.sendingNow)
    ? [...pending.filter((block) => block.sendingNow), ...pending.filter((block) => !block.sendingNow)]
    : pending;
}

/**
 * The transcript in reading order: every message that waited behind a run
 * drawn where the agent took it rather than where it was sent.
 *
 * While it waits the message is drawn at the foot of the transcript, below
 * everything that has arrived, so landing after everything that arrived
 * before the agent took it keeps it exactly where the reader last saw it —
 * including below a run status that came in while it waited, which it was
 * already drawn under. Returns `blocks` itself when nothing moves. Mirrors
 * native `ChatBlock.placedAtDelivery`.
 */
export function placeDeliveredMessages(blocks: ChatBlock[]): ChatBlock[] {
  const moving: Array<{ block: ChatBlock; after: number }> = [];
  for (const block of blocks) {
    if (block.kind !== "user" || !block.delivered || block.landsAfterSeq === undefined) continue;
    if (block.landsAfterSeq > block.seq) moving.push({ block, after: block.landsAfterSeq });
  }
  if (moving.length === 0) return blocks;
  const movingIds = new Set(moving.map(({ block }) => block.id));
  const placed = blocks.filter((block) => !movingIds.has(block.id));
  for (const { block, after } of moving) {
    const index = placed.findLastIndex((candidate) => candidate.seq <= after) + 1;
    placed.splice(index, 0, block);
  }
  return placed;
}

/** A queued message or team message its author deleted, which no reader
 * should see. */
export function isWithdrawnBlock(block: ChatBlock): boolean {
  return (block.kind === "user" || block.kind === "team") && block.withdrawn === true;
}

function findLatestOpenJob(blocks: ChatBlock[]): number {
  for (let index = blocks.length - 1; index >= 0; index--) {
    const block = blocks[index];
    if (block?.kind === "job-status") {
      return isTerminalJobStatus(block.status) ? -1 : index;
    }
    if (block?.kind === "user") return -1;
  }
  return -1;
}
